import classnames from 'classnames';

import style from './RepresentativeCard.module.scss';

import { RepresentativeStatus, RepresentativeType } from 'chaika-enums';
import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { SaveButton } from '@components/ActionButtons/SaveButton';
import { CancelButton } from '@components/ActionButtons/CancelButton';
import { PatientSelect, PatientLabelFormat } from '@components/ModelSelect/PatientSelect';
import { ClickOutsideAlerter } from '@components/ClickOutsideAlerter';
import { FoldingComponent } from '@components/FoldingComponent';
import { OptionType, Select } from '@components/Select';
import { Icon, IconType } from '@components/Icon';
import { EhrPageAttributes } from 'chaika-test-attributes';
import { representativeTypeOption } from '@patient/store/ehr/constants';
import { translate } from '@chaika/i18n/helper';

export interface RepresentativeCardProps {
    cardType: RepresentativeCardType;
    representativeId: Nullable<string>;
    createMode?: boolean;
}

export enum RepresentativeCardType {
    Representative = 'representative',
    Confidant = 'confidant',
}

interface Props extends RepresentativeCardProps, Omit<CreateButtonsProps, 'disableSave'>, ConfirmRepresentativeProps {
    ehrId: string;
    representativeInfo: string;
    type?: RepresentativeType;
    onRepresentativeClick(): void;
    onPatientChange(params: OptionType<string>): void;
    onTypeChange(params: OptionType<RepresentativeType>): void;
    onRemoveClick(): void;
    onCancelCreate(): void;
}

export function RepresentativeCard({
    cardType,
    ehrId,
    representativeInfo,
    type,
    representativeId,
    status,
    onConfirmRepresentative,
    createMode,
    onTypeChange,
    onRepresentativeClick,
    onPatientChange,
    onRemoveClick,
    onSaveClick,
    onCreateNewEhrClick,
    onCancelCreate,
}: Props) {
    const representativeCard = () => {
        return cardType === RepresentativeCardType.Representative ? (
            <ClickOutsideAlerter onClickOutside={createMode ? onCancelCreate : undefined}>
                <div className={classnames([style.root, createMode && style.root_createMode])}>
                    <div className={style.patientSelect}>
                        <PatientSelect
                            dataTest={EhrPageAttributes.RepresentativeSelect}
                            labelFormat={[
                                PatientLabelFormat.RecordId,
                                PatientLabelFormat.FullName,
                                PatientLabelFormat.BirthDay,
                                PatientLabelFormat.Phone,
                            ]}
                            executeId={ehrId}
                            patientId={representativeId}
                            onChange={onPatientChange}
                        />
                    </div>
                    <div className={style.patientSelect}>
                        <Select
                            title={translate('relationshipType')}
                            selectedValue={type}
                            options={representativeTypeOption()}
                            onChange={onTypeChange}
                        />
                    </div>
                    {createMode ? (
                        <CreateButtons
                            disableSave={!representativeId}
                            onSaveClick={onSaveClick}
                            onCreateNewEhrClick={onCreateNewEhrClick}
                        />
                    ) : (
                        <ConfirmRepresentative
                            status={status}
                            onConfirmRepresentative={onConfirmRepresentative}
                        />
                    )}
                </div>
            </ClickOutsideAlerter>
        ) : (
            <></>
        );
    };

    return createMode ? (
        representativeCard()
    ) : (
        <FoldingComponent
            disableOpen={cardType === RepresentativeCardType.Confidant}
            content={representativeInfo}
            additionalCardContent={status === RepresentativeStatus.Confirmed ? ConfirmedStatusIcon() : undefined}
            controlDataTest={EhrPageAttributes.EditRepresentative}
            removeDataTest={EhrPageAttributes.RemoveRepresentative}
            onRemoveClick={onRemoveClick}
            onContentClick={onRepresentativeClick}
        >
            {representativeCard()}
        </FoldingComponent>
    );
}

function ConfirmedStatusIcon() {
    return (
        <div className={style.confirmedIcon}>
            <Icon type={IconType.CHECK} />
        </div>
    );
}

interface CreateButtonsProps {
    disableSave: boolean;
    onSaveClick(): void;
    onCreateNewEhrClick(): void;
}

function CreateButtons({ disableSave, onSaveClick, onCreateNewEhrClick }: CreateButtonsProps) {
    return (
        <div className={style.buttons}>
            <div className={style.buttonWrapper}>
                <SaveButton
                    onSaveClick={onSaveClick}
                    label={translate('assign')}
                    dataTest={EhrPageAttributes.AssignRepresentativeButton}
                    disabled={disableSave}
                />
            </div>
            <div className={style.createEhrButtonWrapper}>
                <CancelButton
                    onCancelClick={onCreateNewEhrClick}
                    label={translate('createEhr')}
                    dataTest={EhrPageAttributes.CreateRepresentativeButton}
                />
            </div>
        </div>
    );
}

interface ConfirmRepresentativeProps {
    status?: RepresentativeStatus;
    onConfirmRepresentative(): void;
}

function ConfirmRepresentative({ status, onConfirmRepresentative }: ConfirmRepresentativeProps) {
    return (
        <div className={style.confirmButton}>
            {status === RepresentativeStatus.NotConfirmed ? (
                <div className={style.buttonWrapper}>
                    <SaveButton
                        onSaveClick={onConfirmRepresentative}
                        label={translate('confirm')}
                        dataTest={EhrPageAttributes.ConfirmRepresentativeButton}
                    />
                </div>
            ) : (
                <div className={style.confirmText}>{translate('documented')}</div>
            )}
        </div>
    );
}
